<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { DayPilot, DayPilotMonth } from '@daypilot/daypilot-lite-vue';
import axios from 'axios';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';

const props = defineProps({
  type: { type: String, required: true, validator: (v) => ['company', 'provider'].includes(v) },
  companyId: { type: [Number, String], required: true },
  providerId: { type: [Number, String], default: null },
  title: { type: String, default: 'Sonderöffnungszeiten Hub' }
});

const emit = defineEmits(['date-selected']);

const calendar = ref(null);
const specialDaysMap = ref({});
const currentYear = ref(new Date().getFullYear());
const currentMonth = ref(new Date().getMonth() + 1);

const years = computed(() => {
  const y = [];
  const start = new Date().getFullYear() - 1;
  for (let i = 0; i < 5; i++) y.push(start + i);
  return y;
});

const months = [
  { label: 'Januar', value: 1 }, { label: 'Februar', value: 2 }, { label: 'März', value: 3 },
  { label: 'April', value: 4 }, { label: 'Mai', value: 5 }, { label: 'Juni', value: 6 },
  { label: 'Juli', value: 7 }, { label: 'August', value: 8 }, { label: 'September', value: 9 },
  { label: 'Oktober', value: 10 }, { label: 'November', value: 11 }, { label: 'Dezember', value: 12 }
];

const escapeHtml = (text) => {
  if (!text) return '';
  return text.toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const config = ref({
  viewType: "Month",
  startDate: DayPilot.Date.today().firstDayOfMonth(),
  locale: "de-de",
  eventHeight: 30,
  headerHeight: 30,
  cellHeight: 60,
  cellHeaderHeight: 25,
  onBeforeCellRender: (args) => {
    const dateStr = args.cell.start.toString("yyyy-MM-dd");
    const special = specialDaysMap.value[dateStr];

    if (!special) return;

    // Colors: Company Orange (#FBBB3A), Provider Pink (#ED9999)
    const isProvider = special.source === 'provider';
    const baseColor = isProvider ? "#ED9999" : "#FBBB3A";
    const lightColor = isProvider ? "#fecaca" : "#FEF3C7";

    const note = special.reason;
    const reasonText = note ? `<div style="font-weight: 500; font-size: 9px; margin-top: 2px; text-transform: none; line-height: 1.2; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word; color: #4b5563;">${escapeHtml(note)}</div>` : '';

    if (special.is_workday) {
      args.cell.properties.cssClass = isProvider ? "special-day-provider" : "special-day-company";
      args.cell.properties.backColor = baseColor;
      args.cell.properties.html = `
        <div class="special-day-label">
          ${special.start_time?.substring(0, 5) || ''}
          ${special.end_time ? ' - ' + special.end_time.substring(0, 5) : ''}
          ${reasonText}
        </div>
      `;
    } else {
      args.cell.properties.cssClass = isProvider ? "special-day-provider-closed" : "special-day-company-closed";
      args.cell.properties.backColor = lightColor;
      args.cell.properties.html = `
        <div class="special-day-label">
          GESCHLOSSEN
          ${reasonText}
        </div>
      `;
    }
  },
  onTimeRangeSelected: (args) => {
    emit('date-selected', args.start.toString("yyyy-MM-dd"));
    calendar.value.control.clearSelection();
  }
});

const fetchSpecialDays = async () => {
  try {
    let endpoint, params;
    
    if (props.type === 'provider') {
      endpoint = '/ajax/admin/provider-special-days/monthly-overview';
      params = {
        provider_id: props.providerId,
        company_id: props.companyId,
        month: currentMonth.value,
        year: currentYear.value
      };
    } else {
      endpoint = '/ajax/admin/company-special-days';
      params = {
        company_id: props.companyId,
        all: 1,
        month: currentMonth.value,
        year: currentYear.value
      };
    }

    const { data } = await axios.get(endpoint, { params });
    const rows = data.data || data;
    
    const map = {};
    rows.forEach(d => {
      if (d.start_date) {
        const start = new Date(d.start_date);
        const end = d.end_date ? new Date(d.end_date) : new Date(d.start_date);
        
        let currentDate = new Date(start);
        while (currentDate <= end) {
          const normalizedDate = currentDate.toLocaleDateString('en-CA');
          // If a provider record already exists, don't overwrite it with a company record
          if (!map[normalizedDate] || d.source === 'provider') {
            map[normalizedDate] = d;
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    });

    specialDaysMap.value = map;
    
    if (calendar.value?.control) {
      calendar.value.control.update();
    }
  } catch (e) {
    console.error('Failed to fetch special days:', e);
  }
};

const navigate = (dir) => {
  let m = currentMonth.value + dir;
  let y = currentYear.value;
  if (m > 12) { m = 1; y++; }
  if (m < 1) { m = 12; y--; }
  currentMonth.value = m;
  currentYear.value = y;
};

watch([currentMonth, currentYear, () => props.providerId], () => {
  const date = new DayPilot.Date(`${currentYear.value}-${String(currentMonth.value).padStart(2, '0')}-01`);
  config.value.startDate = date;
  fetchSpecialDays();
});

onMounted(() => {
  fetchSpecialDays();
});

defineExpose({ refresh: fetchSpecialDays });
</script>

<template>
  <div class="bg-white rounded-xl border border-gray-200 mt-8 overflow-hidden">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
      <div>
        <h3 class="text-lg font-bold text-gray-900">{{ title }}</h3>
        <p class="text-xs text-gray-400">Verwalten von Sonderöffnungszeiten & Ausnahmen</p>
      </div>
      
      <div class="flex items-center gap-2">
        <Button icon="pi pi-chevron-left" text rounded @click="navigate(-1)" />
        <Dropdown v-model="currentMonth" :options="months" optionLabel="label" optionValue="value" class="!border-none !bg-transparent !font-bold" />
        <Dropdown v-model="currentYear" :options="years" class="!border-none !bg-transparent !font-bold" />
        <Button icon="pi pi-chevron-right" text rounded @click="navigate(1)" />
      </div>
    </div>

    <!-- Calendar Area -->
    <div class="p-4">
      <div class="border border-gray-100">
        <DayPilotMonth :config="config" ref="calendar" />
      </div>

      <!-- Legend -->
      <div class="mt-4 flex flex-wrap items-center gap-x-8 gap-y-4 pt-4 border-t border-gray-50">
        <div class="flex items-center gap-2">
          <div class="w-3 h-3 rounded bg-[#FBBB3A] border border-amber-400"></div>
          <span class="text-[9px] text-gray-500 uppercase font-bold">Unternehmens-Arbeitstag</span>
        </div>
        <div class="flex items-center gap-2">
          <div class="w-3 h-3 rounded bg-[#FEF3C7] border border-[#FBBB3A]"></div>
          <span class="text-[9px] text-gray-500 uppercase font-bold">Unternehmens-Freitag</span>
        </div>
        <div v-if="type === 'provider'" class="flex items-center gap-2">
          <div class="w-3 h-3 rounded bg-[#ED9999] border border-red-300"></div>
          <span class="text-[9px] text-gray-500 uppercase font-bold">Mitarbeiter-Arbeitstag</span>
        </div>
        <div v-if="type === 'provider'" class="flex items-center gap-2">
          <div class="w-3 h-3 rounded bg-[#fecaca] border border-[#ED9999]"></div>
          <span class="text-[9px] text-gray-500 uppercase font-bold">Mitarbeiter-Freitag</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.month_default_cell_inner) {
  border-right: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
  background-color: #ffffff;
}

/* Ensure non-business days (weekends) are also white */
:deep(.month_default_cell_nonbusiness .month_default_cell_inner) {
  background-color: #ffffff;
}

/* Company Styles */
:deep(.special-day-company .month_default_cell_inner) {
  background: #FBBB3A !important;
}
:deep(.special-day-company-closed .month_default_cell_inner) {
  border: 3px solid #FBBB3A !important;
  box-sizing: border-box !important;
}

/* Provider Styles */
:deep(.special-day-provider .month_default_cell_inner) {
  background: #ED9999 !important;
}
:deep(.special-day-provider-closed .month_default_cell_inner) {
  background: #FFF1F1 !important;
  border: 3px solid #ED9999 !important;
  box-sizing: border-box !important;
}

:deep(.month_default_header) {
  background-color: #f9fafb !important;
  font-size: 11px !important;
  color: #6b7280 !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  padding: 8px 0 !important;
}

:deep(.month_default_cell_header) {
  font-size: 11px !important;
  color: #9ca3af !important;
  padding: 8px 6px !important;
}

:deep(.special-day-label) {
  position: absolute !important;
  bottom: 6px !important;
  left: 6px !important;
  font-size: 9px !important;
  font-weight: 800 !important;
  color: #1f2937 !important;
  text-transform: uppercase !important;
  pointer-events: none !important;
}
</style>


